import React from 'react'
import CustTable from '../../Common/CustTable'
import { t } from 'i18next'
import { Link } from 'react-router-dom'
import { Button } from 'antd'
import { FormOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { Color } from '../../Common/Color'

const NO_VALUE = '-'

// statuts fournis par glances, du plus au moins critique
const STATUS_STYLES = {
  critical: { color: Color.red, fontWeight: 'bold' },
  warning: { color: '#e8590c' },
  careful: { color: Color.dark_orange }
}

const value = (number, unit, digits = 1) => (
  number === null || number === undefined ? NO_VALUE : `${Number(number).toFixed(digits)}${unit && ` ${unit}`}`
)

const Metric = ({ label, status, children }) => (
  <div><span style={{ opacity: 0.65 }}>{label}</span> <span style={STATUS_STYLES[status]}>{children}</span></div>
)

const MetricDate = ({ date }) => (
  <div style={{ opacity: 0.65 }}>{date ? dayjs(date).format('DD/MM HH:mm') : NO_VALUE}</div>
)

// status computed by the server with the speedtest alert limits (critical < alert, warning < warning)
export const Bandwidth = ({ mbps, status }) => (
  <span data-testid='bandwidth' style={STATUS_STYLES[status]}>{value(mbps, 'Mbps')}</span>
)

const speedtestColumn = (direction) => ({
  title: t(`Admin.GepetoList.table.${direction}`),
  dataIndex: 'last_speedtest_summary',
  key: direction,
  width: 110,
  render: (speedtest) => <Bandwidth mbps={speedtest?.[direction]} status={speedtest?.[`${direction}_status`]} />,
  sorter: (a, b) => (a.last_speedtest_summary?.[direction] || 0) - (b.last_speedtest_summary?.[direction] || 0)
})

const Ping = ({ systemLog }) => {
  if (!systemLog) { return NO_VALUE }
  return (
    <>
      <MetricDate date={systemLog.date} />
      <Metric label={t('Admin.GepetoList.table.rttAvg')}>{value(systemLog.ping?.rtt_avg, 'ms')}</Metric>
      <Metric label={t('Admin.GepetoList.table.rttMdev')}>{value(systemLog.ping?.rtt_mdev, 'ms')}</Metric>
      <Metric label={t('Admin.GepetoList.table.packetLoss')}>{value(systemLog.ping?.packet_loss, '%')}</Metric>
    </>
  )
}

const Glances = ({ systemLog }) => {
  if (!systemLog) { return NO_VALUE }
  const { load, mem, temperature, disk } = systemLog.glances || {}
  const power = systemLog.power || {}
  return (
    <>
      <Metric label={t('Admin.GepetoList.table.load')} status={load?.status}>{value(load?.value, '', 2)}</Metric>
      <Metric label={t('Admin.GepetoList.table.mem')} status={mem?.status}>{value(mem?.value, '%')}</Metric>
      <Metric label={t('Admin.GepetoList.table.temperature')} status={temperature?.status}>
        {value(temperature?.value, '°C', 0)}
      </Metric>
      <Metric label={t('Admin.GepetoList.table.disk')} status={disk?.status}>{value(disk?.value, '%')}</Metric>
      <Metric label={t('Admin.GepetoList.table.underVoltage')} status={power.status}>
        {value(power.under_voltage_events, '', 0)}
      </Metric>
    </>
  )
}

const columns = () => [
  {
    title: t('Admin.GepetoList.table.name'),
    dataIndex: 'name',
    key: 'name',
    width: 200,
    render: (_, o) => o.box.name,
    sorter: (a, b) => a.box.name.localeCompare(b.box.name)
  },
  {
    title: t('Admin.GepetoList.table.version'),
    dataIndex: 'version',
    key: 'version',
    width: 120,
    render: (version) => version.gepeto
  },
  {
    title: t('Admin.GepetoList.table.ip'),
    dataIndex: 'ip',
    key: 'ip',
    width: 120,
    render: (_, o) => o.ip,
    sorter: (a, b) => a.ip - b.ip
  },
  {
    title: t('Admin.GepetoList.table.speedtest'),
    dataIndex: 'last_speedtest_summary',
    key: 'speedtest',
    width: 110,
    render: (speedtest) => <MetricDate date={speedtest?.date} />,
    sorter: (a, b) => dayjs(a.last_speedtest_summary?.date || 0) - dayjs(b.last_speedtest_summary?.date || 0)
  },
  speedtestColumn('download'),
  speedtestColumn('upload'),
  {
    title: t('Admin.GepetoList.table.ping'),
    dataIndex: 'last_system_summary',
    key: 'ping',
    width: 170,
    render: (systemLog) => <Ping systemLog={systemLog} />,
    sorter: (a, b) => (a.last_system_summary?.ping?.rtt_avg || 0) - (b.last_system_summary?.ping?.rtt_avg || 0)
  },
  {
    title: t('Admin.GepetoList.table.sessions'),
    dataIndex: 'last_system_summary',
    key: 'sessions',
    width: 100,
    render: (systemLog) => systemLog?.sessions ?? NO_VALUE,
    sorter: (a, b) => (a.last_system_summary?.sessions || 0) - (b.last_system_summary?.sessions || 0)
  },
  {
    title: t('Admin.GepetoList.table.glances'),
    dataIndex: 'last_system_summary',
    key: 'glances',
    width: 160,
    render: (systemLog) => <Glances systemLog={systemLog} />,
    sorter: (a, b) => (
      (a.last_system_summary?.glances?.load?.value || 0) - (b.last_system_summary?.glances?.load?.value || 0)
    )
  },
  {
    width: 50,
    key: 'action',
    render: (_, o) => <Link to={`/admin/gepetos/${o.id}`}><Button shape="circle" icon={<FormOutlined />} /></Link>
  }
]
export default function GepetoList ({ gepetos }) {
  const rowClassName = (gepeto) => {
    return gepeto.newAlert ? 'new-alert' : ''
  }
  return (
    <CustTable
      rowClassName={rowClassName}
      height={700}
      horizontalScroll={1250}
      initColumns={columns()}
      dataSource={gepetos}
    />
  )
}
